import LabelRow from "@components/rowcomponents/LabelRow";
import { formatDisplayDate, parseDisplayDate } from "@utils/safeDate";
import { useState } from "react";

type InvertibleDateTimeProps = {
  value?: string | null;
  dateLabel?: string;
  timeLabel?: string;
};

function formatTradeDate(date: Date, useUtc: boolean): string {
  return formatDisplayDate(date, "en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: useUtc ? "UTC" : undefined,
  });
}

function formatTradeTime(date: Date, useUtc: boolean): string {
  return formatDisplayDate(date, "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: useUtc ? "UTC" : undefined,
  });
}

const ToggleValue = ({
  text,
  useUtc,
  onToggle,
}: {
  text: string;
  useUtc: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    title={useUtc ? "Switch to local time" : "Switch to UTC"}
    className="flex flex-row gap-[4px] items-center cursor-pointer rounded-md px-1 py-0.5 -mx-1 hover:bg-bg-page-body transition-colors"
  >
    <span className="text-text-base text-[12px] font-normal">{text}</span>
    <span className="text-text-secondary text-[9px] font-semibold uppercase">
      {useUtc ? "UTC" : "Local"}
    </span>
    <span className="material-symbols-outlined text-[12px] text-icon-base">
      sync_alt
    </span>
  </button>
);

const InvertibleDateTime = ({
  value,
  dateLabel = "Trade Date",
  timeLabel = "Trade Time",
}: InvertibleDateTimeProps) => {
  const [useUtc, setUseUtc] = useState(false);
  const date = parseDisplayDate(value);

  if (!date) {
    return (
      <>
        <LabelRow label={dateLabel} value="- -" />
        <LabelRow label={timeLabel} value="- -" />
      </>
    );
  }

  const toggle = () => setUseUtc((current) => !current);

  return (
    <>
      <LabelRow
        label={dateLabel}
        valueComp={
          <ToggleValue
            text={formatTradeDate(date, useUtc)}
            useUtc={useUtc}
            onToggle={toggle}
          />
        }
      />
      <LabelRow
        label={timeLabel}
        valueComp={
          <ToggleValue
            text={formatTradeTime(date, useUtc)}
            useUtc={useUtc}
            onToggle={toggle}
          />
        }
      />
    </>
  );
};

export default InvertibleDateTime;
